// CRM Preview modal — analytical profile view: hero photo gallery, identity,
// quick stats, contact strip, communication history with rich Log Interaction
// form, linked catalog videos and studio collabs.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import {
  X, Pencil, Trash2, Plus, Check,
  ChevronLeft, ChevronRight, MapPin, Globe, Mail, Phone, ExternalLink,
  Languages as LangIcon, Ruler, Weight as WeightIcon, Users,
  MessageCircle, Film, Building2, DollarSign,
  Sparkles, Loader2, Brain,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  TYPE_HEX, STATUS_HEX, STATUS_LABEL, typeLabel,
  FOLLOWUP_KINDS, FU_DIRECTIONS, FU_OUTCOMES,
  getRecordStatuses, primaryStatus,
} from "./constants";
import { calcAge, formatRelative, formatInteractionDate, safeRelative } from "./helpers";

const formatDateOnly = (value) => value ? String(value).slice(0, 10) : "";
const formatSyncTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

function mergeHistory(...lists) {
  const seen = new Set();
  return lists.flatMap((list) => Array.isArray(list) ? list : []).filter((item) => {
    const key = item.id || JSON.stringify([item.n, item.date, item.location, item.notes, item.video_id]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function CRMPreview({ person, onClose, onEdit, onDelete, onLocalUpdate, canDelete = true }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [videos, setVideos] = useState([]);
  const [studios, setStudios] = useState([]);
  const [content360, setContent360] = useState(null);
  const [loadingV, setLoadingV] = useState(true);
  const [loadingS, setLoadingS] = useState(true);
  const [loading360, setLoading360] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [historySearch, setHistorySearch] = useState("");
  const [advice, setAdvice] = useState(null);
  const [askingAdvice, setAskingAdvice] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [logForm, setLogForm] = useState({
    kind: "dm",
    date: new Date().toISOString().slice(0, 16),
    platform: "",
    direction: "outbound",
    outcome: "neutral",
    summary: "",
    note: "",
    reply: "",
    conversation: "",
    follow_up_date: "",
    linked_video_id: "",
    linked_studio_id: "",
  });
  const [savingFu, setSavingFu] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    setLoadingV(true); setLoadingS(true); setLoading360(true);
    api.get(`/api/videos/by-crm/${person.id}`)
      .then(r => { if (active) setVideos(r.data); })
      .catch(() => { if (active) setVideos([]); })
      .finally(() => { if (active) setLoadingV(false); });
    api.get(`/api/studios/by-crm/${person.id}`)
      .then(r => { if (active) setStudios(r.data); })
      .catch(() => { if (active) setStudios([]); })
      .finally(() => { if (active) setLoadingS(false); });
    api.get(`/api/crm/${person.id}/content-360`)
      .then(r => { if (active) setContent360(r.data); })
      .catch(() => { if (active) setContent360(null); })
      .finally(() => { if (active) setLoading360(false); });
    return () => { active = false; };
  }, [person.id]);

  const photos = person.photos || [];
  const age = calcAge(person.date_of_birth);
  const typeColor = TYPE_HEX[person.type] || "#94a3b8";
  const recordStatuses = getRecordStatuses(person);
  const primary = primaryStatus(recordStatuses);
  const statusColor = STATUS_HEX[primary] || "#94a3b8";
  const initials = (person.stage_name || "?").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const totalStudioRev = studios.reduce(
    (acc, s) => acc + (s.scenes || []).reduce((a, sc) => a + (Number(sc.revenue) || 0), 0),
    0
  );
  const fuPending = (person.follow_ups || []).filter(f => f.follow_up_date && !f.done).length;
  const sortedFu = [...(person.follow_ups || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const visibleFollowUps = sortedFu.filter((interaction) => {
    const searchText = `${interaction.summary || ""} ${interaction.note || ""} ${interaction.reply || ""} ${interaction.platform || ""}`.toLowerCase();
    if (historySearch.trim() && !searchText.includes(historySearch.trim().toLowerCase())) return false;
    if (historyFilter === "needs_follow_up") return Boolean(interaction.follow_up_date && !interaction.done);
    if (historyFilter === "positive" || historyFilter === "negative") return interaction.outcome === historyFilter;
    return historyFilter === "all" || interaction.kind === historyFilter;
  });
  const isEventOrganizer = person.type === "Event Organizer";
  const eventHistory = isEventOrganizer
    ? mergeHistory(person.type_details?.history, person.shoots, person.shows)
    : (person.shows || []);
  const historySections = [
    { kind: "Shoot", items: isEventOrganizer ? [] : (person.shoots || []) },
    { kind: "Event", items: eventHistory },
  ].filter(section => section.items.length > 0);
  const isPerformer = /performer|actor|model|talent/i.test(person.type || "");
  const hasFanFinancialRelationship = !isPerformer && (
    /fan|customer|subscriber|client/i.test(person.type || "")
    || Number(content360?.totals?.of_purchases_count || 0) > 0
    || Number(content360?.totals?.of_purchases_gross_usd || 0) > 0
  );
  const extensionPosts = (content360?.social_posts || []).filter(post => post.source === "extension_synced");
  const extensionLastSynced = content360?.totals?.extension_last_synced_at
    || extensionPosts.map(post => post.last_synced_at).filter(Boolean).sort().slice(-1)[0];

  const nextPhoto = () => photos.length > 0 && setPhotoIdx((i) => (i + 1) % photos.length);
  const prevPhoto = () => photos.length > 0 && setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);

  const persistFollowUps = async (next) => {
    try {
      const updated = await api.put(`/api/crm/${person.id}`, { follow_ups: next });
      onLocalUpdate(updated.data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  const resetLogForm = () => {
    setLogForm({
      kind: "dm",
      date: new Date().toISOString().slice(0, 16),
      platform: "",
      direction: "outbound",
      outcome: "neutral",
      summary: "",
      note: "",
      reply: "",
      conversation: "",
      follow_up_date: "",
      linked_video_id: "",
      linked_studio_id: "",
    });
    setEditingInteraction(null);
    setAiSuggestion(null);
  };

  const addFollowUp = async () => {
    const summary = (logForm.summary || "").trim();
    if (!summary) {
      toast.error("Summary / Subject is required");
      return;
    }
    setSavingFu(true);
    const isoDate = logForm.date
      ? new Date(logForm.date).toISOString()
      : new Date().toISOString();
    
    if (editingInteraction) {
      // Update existing interaction
      const next = (person.follow_ups || []).map(it => 
        it.id === editingInteraction ? {
          ...it,
          kind: logForm.kind,
          platform: logForm.platform || undefined,
          direction: logForm.direction || undefined,
          outcome: logForm.outcome || undefined,
          summary,
          note: (logForm.note || "").trim(),
          reply: (logForm.reply || "").trim() || undefined,
          date: isoDate,
          follow_up_date: logForm.follow_up_date || undefined,
          linked_video_id: logForm.linked_video_id || undefined,
          linked_studio_id: logForm.linked_studio_id || undefined,
        } : it
      );
      if (!await persistFollowUps(next)) {
        setSavingFu(false);
        return;
      }
      toast.success("Interaction updated");
      setEditingInteraction(null);
    } else {
      // Create new interaction
      const newFu = {
        id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        kind: logForm.kind,
        platform: logForm.platform || undefined,
        direction: logForm.direction || undefined,
        outcome: logForm.outcome || undefined,
        summary,
        note: (logForm.note || "").trim(),
        reply: (logForm.reply || "").trim() || undefined,
        date: isoDate,
        done: false,
        follow_up_date: logForm.follow_up_date || undefined,
        linked_video_id: logForm.linked_video_id || undefined,
        linked_studio_id: logForm.linked_studio_id || undefined,
      };
      Object.keys(newFu).forEach(k => newFu[k] === undefined && delete newFu[k]);
      const next = [...(person.follow_ups || []), newFu];
      if (!await persistFollowUps(next)) {
        setSavingFu(false);
        return;
      }
      toast.success("Interaction logged");
    }
    resetLogForm();
    setShowLog(false);
    setSavingFu(false);
  };

  const toggleFollowUp = async (id) => {
    const next = (person.follow_ups || []).map(it => it.id === id ? { ...it, done: !it.done } : it);
    await persistFollowUps(next);
  };

  const deleteFollowUp = async (id) => {
    const next = (person.follow_ups || []).filter(it => it.id !== id);
    await persistFollowUps(next);
  };
  
  const editInteraction = (interaction) => {
    setEditingInteraction(interaction.id);
    setLogForm({
      kind: interaction.kind || "dm",
      date: interaction.date || new Date().toISOString().slice(0, 16),
      platform: interaction.platform || "",
      direction: interaction.direction || "outbound",
      outcome: interaction.outcome || "neutral",
      summary: interaction.summary || "",
      note: interaction.note || "",
      reply: interaction.reply || "",
      conversation: interaction.conversation || "",
      follow_up_date: interaction.follow_up_date || "",
      linked_video_id: interaction.linked_video_id || "",
      linked_studio_id: interaction.linked_studio_id || "",
    });
    setShowLog(true);
  };
  
  const duplicateInteraction = (interaction) => {
    setEditingInteraction(null);
    setLogForm({
      ...interaction,
      date: new Date().toISOString().slice(0, 16),
      summary: `Copy of: ${interaction.summary}`,
    });
    setShowLog(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center"
      data-testid="crm-preview" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full md:max-w-6xl mx-auto md:my-6 md:rounded-2xl overflow-hidden flex flex-col max-h-[100vh] md:max-h-[94vh]"
        style={{
          background: "linear-gradient(180deg, var(--gio-surface) 0%, var(--gio-surface-hi) 100%)",
          border: "1px solid var(--gio-border)",
          boxShadow: `0 30px 80px -20px ${typeColor}40`,
        }}>
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0 bg-gradient-to-r from-purple-900/20 to-pink-900/20 backdrop-blur-sm" style={{ borderColor: "rgba(168, 85, 247, 0.3)" }}>
          <div className="flex items-center gap-3">
            <div className="overline font-bold tracking-wider" style={{ color: '#FF10F0', fontSize: '0.75rem' }}>
              PROFILE PREVIEW
            </div>
            <span className="px-2 py-1 rounded-full text-xs font-semibold" style={{ 
              background: `${typeColor}20`, 
              color: typeColor,
              border: `1px solid ${typeColor}40`
            }}>
              {typeLabel(person.type).toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} data-testid="crm-preview-edit"
              className="px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-300 transition-all inline-flex items-center gap-2 text-sm font-medium">
              <Pencil size={14} /> Edit
            </button>
            {canDelete && <button onClick={onDelete} data-testid="crm-preview-delete"
              className="p-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 transition-all" title="Delete">
              <Trash2 size={16} />
            </button>}
            <button onClick={onClose} data-testid="crm-preview-close"
              className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-white transition-all">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* HERO */}
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative bg-black aspect-[4/5] md:aspect-auto md:min-h-[560px]">
              {photos.length > 0 ? (
                <>
                  <img src={photos[photoIdx]} alt={person.stage_name}
                    data-testid="crm-preview-hero-photo"
                    className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0))" }} />
                  {photos.length > 1 && (
                    <>
                      <button onClick={prevPhoto} data-testid="crm-preview-prev"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>
                        <ChevronLeft size={20} />
                      </button>
                      <button onClick={nextPhoto} data-testid="crm-preview-next"
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>
                        <ChevronRight size={20} />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {photos.map((_, i) => (
                          <button key={i} onClick={() => setPhotoIdx(i)}
                            data-testid={`crm-preview-dot-${i}`}
                            className="rounded-full transition-all"
                            style={{
                              width: i === photoIdx ? 24 : 8, height: 8,
                              background: i === photoIdx ? "#fff" : "rgba(255,255,255,0.4)",
                            }} />
                        ))}
                      </div>
                    </>
                  )}
                  <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full"
                    style={{ background: "rgba(0,0,0,0.6)", color: "#fff", backdropFilter: "blur(8px)" }}>
                    {photoIdx + 1} / {photos.length}
                  </span>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${typeColor}30 0%, ${typeColor}05 100%)` }}>
                  <div className="w-40 h-40 rounded-full flex items-center justify-center font-heading text-7xl shadow-2xl"
                    style={{ background: `linear-gradient(135deg, ${typeColor} 0%, ${typeColor}aa 100%)`, color: "#fff" }}>
                    {initials}
                  </div>
                </div>
              )}
              {photos.length > 1 && (
                <div className="absolute left-3 right-3 bottom-12 hidden md:flex gap-1.5 overflow-x-auto pb-1">
                  {photos.map((src, i) => (
                    <button key={i} onClick={() => setPhotoIdx(i)}
                      data-testid={`crm-preview-thumb-${i}`}
                      className="shrink-0 rounded-md overflow-hidden transition-all"
                      style={{
                        width: 56, height: 56,
                        boxShadow: i === photoIdx ? `0 0 0 2px #fff` : `0 0 0 1px rgba(255,255,255,0.25)`,
                        opacity: i === photoIdx ? 1 : 0.65,
                      }}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 md:p-8 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                    style={{ background: typeColor, color: "#fff" }}>
                    {typeLabel(person.type)}
                  </span>
                  {recordStatuses.map((status) => (
                    <span key={status} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ background: `${STATUS_HEX[status] || statusColor}22`, color: STATUS_HEX[status] || statusColor, border: `1px solid ${STATUS_HEX[status] || statusColor}55` }}>
                      {STATUS_LABEL[status] || status.replace(/_/g, " ")}
                    </span>
                  ))}
                  {person.source && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted)", border: "1px solid var(--gio-border)" }}>
                      via {person.source}
                    </span>
                  )}
                  {fuPending > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full inline-flex items-center gap-1"
                      style={{ background: "#f97316", color: "#fff" }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> {fuPending} TODO
                    </span>
                  )}
                </div>
                <div>
                  <h2 className="font-heading text-4xl md:text-5xl leading-none" style={{ color: "var(--gio-text)" }}
                    data-testid="crm-preview-name">
                    {person.stage_name}
                    {age && <span className="ml-3 text-2xl font-mono-ui" style={{ color: "var(--gio-muted-2)" }}>· {age}</span>}
                  </h2>
                  {person.legal_name && person.legal_name !== person.stage_name && (
                    <div className="text-[13px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                      Legal: {person.legal_name}
                    </div>
                  )}
                </div>
                {(person.country || person.city) && (
                  <div className="inline-flex items-center gap-2 text-[13px]" style={{ color: "var(--gio-muted)" }}>
                    <MapPin size={14} /> {[person.city, person.country].filter(Boolean).join(", ")}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                  <PStat label="Position" value={person.position} icon={Users} />
                  <PStat label="Body" value={person.body_type} icon={WeightIcon} />
                  <PStat label="Dick" value={person.dick_size && `${person.dick_size}cm`} icon={Ruler} highlight />
                  <PStat label="Height" value={person.height} icon={Ruler} />
                  <PStat label="Weight" value={person.weight && `${person.weight}kg`} icon={WeightIcon} />
                  <PStat label="Ethnicity" value={person.ethnicity} icon={Globe} />
                </div>
                {person.languages && (
                  <div className="pt-1">
                    <div className="overline mb-1.5 inline-flex items-center gap-1.5"><LangIcon size={11} /> LANGUAGES</div>
                    <div className="flex flex-wrap gap-1.5">
                      {person.languages.split(/[,;]/).map((l, i) => l.trim() && (
                        <span key={i} className="text-[11px] px-2 py-0.5 rounded"
                          style={{ background: "var(--gio-surface-hi)", border: "1px solid var(--gio-border)", color: "var(--gio-text)" }}>
                          {l.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-3 border-t" style={{ borderColor: "var(--gio-border)" }}>
                {(person.email || person.phone) && (
                  <div className="flex flex-wrap gap-3 text-[12px]">
                    {person.email && (
                      <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 hover:underline"
                        data-testid="crm-preview-email"
                        style={{ color: "var(--gio-text)" }}>
                        <Mail size={12} /> {person.email}
                      </a>
                    )}
                    {person.phone && (
                      <a href={`tel:${person.phone}`} className="inline-flex items-center gap-1.5 hover:underline"
                        data-testid="crm-preview-phone"
                        style={{ color: "var(--gio-text)" }}>
                        <Phone size={12} /> {person.phone}
                      </a>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {person.twitter && (
                    <SocialChip href={`https://x.com/${person.twitter.replace(/^@/, "")}`} label={`𝕏 @${person.twitter.replace(/^@/, "")}`} testid="crm-preview-twitter" />
                  )}
                  {person.instagram && (
                    <SocialChip href={`https://instagram.com/${person.instagram.replace(/^@/, "")}`} label={`IG @${person.instagram.replace(/^@/, "")}`} color="#ec4899" testid="crm-preview-ig" />
                  )}
                  {person.onlyfans && (
                    <SocialChip href={`https://onlyfans.com/${person.onlyfans.replace(/^@/, "")}`} label={`OF ${person.onlyfans.replace(/^@/, "")}`} color="#00AFF0" testid="crm-preview-of" />
                  )}
                  {person.justforfans && (
                    <SocialChip href={`https://justfor.fans/${person.justforfans.replace(/^@/, "")}`} label={`JFF ${person.justforfans.replace(/^@/, "")}`} color="#f59e0b" testid="crm-preview-jff" />
                  )}
                  {!person.type?.includes("Studio") && (
                    <button
                      onClick={() => window.open('https://faphouse.com/go/invite', '_blank')}
                      className="text-xs px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1.5 transition-all hover:scale-105"
                      style={{ 
                        background: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)",
                        color: "#fff",
                        boxShadow: "0 0 20px rgba(139,92,246,0.4)"
                      }}
                      data-testid="crm-preview-faphouse-invite"
                    >
                      🔥 Invite to Faphouse
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(person.kinks || person.boundaries || person.notes) && (
            <div className="px-6 md:px-8 py-5 border-t grid md:grid-cols-3 gap-4" style={{ borderColor: "var(--gio-border)" }}>
              <PBlock label="Kinks / Interests" value={person.kinks} accent="#ec4899" />
              <PBlock label="Boundaries / Limits" value={person.boundaries} accent="#f59e0b" />
              <PBlock label="Notes" value={person.notes} accent="var(--gio-secondary)" />
            </div>
          )}

          {/* Shoot and event history timelines */}
          {historySections.map(({ kind, items }) => (
            <div key={kind} className="px-6 md:px-8 py-5 border-t" style={{ borderColor: "var(--gio-border)" }} data-testid={`crm-preview-${kind.toLowerCase()}s`}>
              <div className="overline mb-3 inline-flex items-center gap-2">
                <Film size={12} /> {kind.toUpperCase()} HISTORY · {items.length}
              </div>
              <div className="space-y-2">
                {[...items].sort((a, b) => (a.n || 0) - (b.n || 0)).map((s) => (
                  <div key={s.id || `${kind}-${s.n}`} data-testid={`crm-preview-${kind.toLowerCase()}-${s.n}`}
                    className="flex items-start gap-3 p-2.5 rounded border"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                    <span className="text-[11px] font-bold px-2 py-1 rounded-full shrink-0"
                      style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                      #{s.n}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[12px]">
                        {s.date && <span className="font-mono-ui font-bold" style={{ color: "var(--gio-text)" }}>{s.date}</span>}
                        {s.location && <span className="inline-flex items-center gap-1" style={{ color: "var(--gio-muted-2)" }}><MapPin size={10} /> {s.location}</span>}
                      </div>
                      {s.notes && <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted)" }}>{s.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {isPerformer && (
            <div className="px-6 md:px-8 py-5 border-t" style={{ borderColor: "var(--gio-border)" }} data-testid="crm-performer-operations">
              <div className="overline mb-3 inline-flex items-center gap-2"><Film size={12} /> TALENT &amp; COLLABORATION OPERATIONS</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <TalentSignal label="CRM status" value={recordStatuses.map((status) => STATUS_LABEL[status] || status).join(" • ") || "Not recorded"} />
                <TalentSignal label="Past shoots" value={person.shoots?.length || 0} />
                <TalentSignal label="Catalog videos" value={videos.length} />
                <TalentSignal label="Open follow-ups" value={fuPending} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg border p-3" style={{ borderColor: "var(--gio-border)" }}><strong style={{ color: "var(--gio-text)" }}>Next shoot</strong><div className="mt-1" style={{ color: "var(--gio-muted-2)" }}>{person.shoot_date || "No scheduled shoot recorded"}</div></div>
                <div className="rounded-lg border p-3" style={{ borderColor: "var(--gio-border)" }}><strong style={{ color: "var(--gio-text)" }}>Collaboration links</strong><div className="mt-1" style={{ color: "var(--gio-muted-2)" }}>{studios.length + (content360?.studio_collabs?.length || 0)} studio/collaboration records · {content360?.collaborators?.length || 0} connected cast records</div></div>
                <div className="rounded-lg border p-3" style={{ borderColor: "var(--gio-border)" }}><strong style={{ color: "var(--gio-text)" }}>Workflow gaps</strong><div className="mt-1" style={{ color: "var(--gio-muted-2)" }}>Availability calendar, contracts, ID/verification, and booking automation are not available in the current CRM contract.</div></div>
              </div>
            </div>
          )}

          {/* Communication history + Log Interaction */}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline inline-flex items-center gap-2">
                <MessageCircle size={12} /> COMMUNICATION HISTORY · {person.follow_ups?.length || 0}
                {fuPending > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded ml-1"
                    style={{ background: "#f97316", color: "#fff" }}>{fuPending} pending</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={async () => {
                    setAskingAdvice(true);
                    try {
                      const r = await api.post("/api/copilot/crm/advise", { person_id: person.id });
                      setAdvice(r.data);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Advisor failed");
                    } finally { setAskingAdvice(false); }
                  }}
                  type="button"
                  data-testid="crm-preview-ask-advisor"
                  disabled={askingAdvice}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded border hover:opacity-80 transition"
                  style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }}>
                  {askingAdvice ? <Loader2 size={11} className="animate-spin" /> : <Brain size={11} />}
                  Ask AI Advisor
                </button>
                <button onClick={() => setShowLog(true)} type="button"
                  data-testid="crm-preview-fu-open"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-2 transition-all shadow-lg shadow-purple-500/30">
                  <Plus size={14} /> Log New Interaction
                </button>
              </div>
            </div>

            {/* AI Advisor output */}
            {advice && (
              <div className="mb-4 p-4 rounded-lg border" data-testid="crm-preview-advice"
                style={{ borderColor: "var(--gio-primary)", background: "var(--gio-primary-soft)" }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gio-primary)" }}>
                    <Brain size={12} /> AI ADVISOR · {advice.interactions_considered} interactions analyzed
                  </div>
                  <button onClick={() => setAdvice(null)} type="button"
                    className="text-zinc-500 hover:text-white"><X size={14} /></button>
                </div>
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed" style={{ color: "var(--gio-text)" }}>
                  {advice.advice}
                </div>
              </div>
            )}

            {showLog && (
              <div className="mb-4 p-4 rounded-lg border" data-testid="crm-preview-fu-form"
                style={{ borderColor: "var(--gio-secondary)", background: "var(--gio-surface)" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading text-[15px]" style={{ color: "var(--gio-text)" }}>
                    {editingInteraction ? 'Edit' : 'Log'} Interaction with <span style={{ color: "var(--gio-secondary)" }}>{person.stage_name}</span>
                  </div>
                  <button onClick={() => { setShowLog(false); resetLogForm(); }} type="button"
                    className="text-zinc-500 hover:text-white"><X size={16} /></button>
                </div>

                {/* PASTE & AI-SUMMARIZE — top-of-form shortcut that fills the rest */}
                <div className="mb-4 p-3 rounded-lg border"
                  style={{ borderColor: "var(--gio-primary)", background: "var(--gio-primary-soft)" }}>
                  <div className="overline mb-1.5 inline-flex items-center gap-1.5"
                    style={{ color: "var(--gio-primary)" }}>
                    ✨ PASTE CONVERSATION (optional)
                  </div>
                  <textarea value={logForm.conversation || ""} rows={4}
                    onChange={(e) => setLogForm({ ...logForm, conversation: e.target.value })}
                    placeholder="Paste the entire DM thread / email / chat here. AI will extract a structured summary, your message, their reply, kind, platform & outcome — fills the form below."
                    data-testid="crm-preview-fu-conversation"
                    className="gio-input w-full text-[12px]" />
                  <button type="button"
                    onClick={async () => {
                      const text = (logForm.conversation || "").trim();
                      if (!text) { toast.error("Paste a conversation first"); return; }
                      setSummarizing(true);
                      try {
                        const r = await api.post("/api/copilot/crm/summarize-conversation", {
                          conversation: text,
                          person_name: person.stage_name,
                          person_type: person.type,
                        });
                        setAiSuggestion({
                          summary: r.data.summary || "",
                          note: r.data.note || "",
                          reply: r.data.reply || "",
                          kind: r.data.kind || "",
                          platform: r.data.platform || "",
                          direction: r.data.direction || "",
                          outcome: r.data.outcome || "",
                          follow_up_date: r.data.follow_up_date || "",
                        });
                        toast.success("Suggestions ready for review");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Summarize failed");
                      } finally {
                        setSummarizing(false);
                      }
                    }}
                    disabled={summarizing}
                    data-testid="crm-preview-fu-summarize"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-mono-ui uppercase tracking-wider hover:opacity-80 transition"
                    style={{ background: "var(--gio-primary)", color: "#000" }}>
                    {summarizing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {summarizing ? "Summarizing…" : "Summarize with AI"}
                  </button>
                </div>

                {aiSuggestion && (
                  <div className="mb-4 border p-3" style={{ borderColor: "var(--gio-primary)", background: "var(--gio-primary-soft)" }} data-testid="crm-preview-ai-suggestions">
                    <div className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-primary)" }}>AI suggestions · review before applying</div>
                    <div className="space-y-1 text-xs" style={{ color: "var(--gio-text)" }}>
                      {[["summary", "Summary"], ["direction", "Direction"], ["outcome", "Outcome"], ["platform", "Platform"], ["note", "Notes"], ["reply", "Reply"], ["follow_up_date", "Follow-up"]]
                        .filter(([key]) => aiSuggestion[key])
                        .map(([key, label]) => <div key={key}><span style={{ color: "var(--gio-muted)" }}>{label}: </span>{aiSuggestion[key]}</div>)}
                    </div>
                    <button type="button" onClick={() => {
                      setLogForm((current) => Object.fromEntries(Object.entries(current).map(([key, value]) => [key, value || aiSuggestion[key] || value])));
                      setAiSuggestion(null);
                    }} className="btn-ghost mt-3 text-xs" data-testid="crm-preview-ai-apply">Apply suggestions to blank fields</button>
                  </div>
                )}

                <div className="mb-3">
                  <div className="overline mb-1.5">Type</div>
                  <div className="flex flex-wrap gap-1.5">
                    {FOLLOWUP_KINDS.map(k => {
                      const Icon = k.icon;
                      const active = logForm.kind === k.v;
                      return (
                        <button key={k.v} type="button" onClick={() => setLogForm({ ...logForm, kind: k.v })}
                          data-testid={`crm-preview-fu-kind-${k.v}`}
                          className="px-2.5 py-1.5 text-[11px] font-mono-ui uppercase tracking-wider border inline-flex items-center gap-1.5 rounded transition"
                          style={{
                            borderColor: active ? "var(--gio-primary)" : "var(--gio-border)",
                            color: active ? "var(--gio-primary)" : "var(--gio-muted)",
                            background: active ? "var(--gio-primary-soft)" : "transparent",
                          }}>
                          <Icon size={11} /> {k.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <label className="block">
                    <div className="overline mb-1.5">Date &amp; Time</div>
                    <input type="datetime-local" value={logForm.date}
                      onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
                      data-testid="crm-preview-fu-date"
                      className="gio-input w-full" />
                  </label>
                  <label className="block">
                    <div className="overline mb-1.5">Platform</div>
                    <input value={logForm.platform}
                      onChange={(e) => setLogForm({ ...logForm, platform: e.target.value })}
                      placeholder="Gmail, Telegram, IG DM, X DM…"
                      data-testid="crm-preview-fu-platform"
                      list="crm-platform-suggestions"
                      className="gio-input w-full" />
                    <datalist id="crm-platform-suggestions">
                      {["X", "Instagram", "OnlyFans", "Telegram", "WhatsApp", "Email", "Phone"].map((platform) => <option key={platform} value={platform} />)}
                    </datalist>
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <label className="block">
                    <div className="overline mb-1.5">Direction</div>
                    <select value={logForm.direction}
                      onChange={(e) => setLogForm({ ...logForm, direction: e.target.value })}
                      data-testid="crm-preview-fu-direction"
                      className="gio-input w-full">
                      {FU_DIRECTIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <div className="overline mb-1.5">Outcome</div>
                    <select value={logForm.outcome}
                      onChange={(e) => setLogForm({ ...logForm, outcome: e.target.value })}
                      data-testid="crm-preview-fu-outcome"
                      className="gio-input w-full">
                      {FU_OUTCOMES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block mb-3">
                  <div className="overline mb-1.5">Summary / Subject *</div>
                  <input value={logForm.summary}
                    onChange={(e) => setLogForm({ ...logForm, summary: e.target.value })}
                    placeholder="Brief summary of the interaction…"
                    data-testid="crm-preview-fu-summary"
                    className="gio-input w-full" />
                </label>
                <label className="block mb-3">
                  <div className="overline mb-1.5">Notes / Details</div>
                  <textarea value={logForm.note} rows={3}
                    onChange={(e) => setLogForm({ ...logForm, note: e.target.value })}
                    placeholder="Full notes, key points, or email body…"
                    data-testid="crm-preview-fu-note"
                    className="gio-input w-full" />
                </label>
                <label className="block mb-3">
                  <div className="overline mb-1.5 inline-flex items-center gap-1.5">
                    💬 THEIR REPLY / RESPONSE
                  </div>
                  <textarea value={logForm.reply || ""} rows={3}
                    onChange={(e) => setLogForm({ ...logForm, reply: e.target.value })}
                    placeholder="What did they say back? Their answer, counter-offer, questions…"
                    data-testid="crm-preview-fu-reply"
                    className="gio-input w-full" />
                </label>
                <div className="grid sm:grid-cols-3 gap-3 mb-4">
                  <label className="block">
                    <div className="overline mb-1.5">Link to Video</div>
                    <select value={logForm.linked_video_id}
                      onChange={(e) => setLogForm({ ...logForm, linked_video_id: e.target.value })}
                      data-testid="crm-preview-fu-link-video"
                      className="gio-input w-full">
                      <option value="">None</option>
                      {videos.map(v => <option key={v.id} value={v.id}>{(v.title || "").slice(0, 40)}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <div className="overline mb-1.5">Link to Studio</div>
                    <select value={logForm.linked_studio_id}
                      onChange={(e) => setLogForm({ ...logForm, linked_studio_id: e.target.value })}
                      data-testid="crm-preview-fu-link-studio"
                      className="gio-input w-full">
                      <option value="">None</option>
                      {studios.map(s => <option key={s.id} value={s.id}>{s.studio_name}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <div className="overline mb-1.5">Follow-up Date (optional)</div>
                    <input type="date" value={logForm.follow_up_date}
                      onChange={(e) => setLogForm({ ...logForm, follow_up_date: e.target.value })}
                      data-testid="crm-preview-fu-followup-date"
                      className="gio-input w-full" />
                  </label>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setShowLog(false); resetLogForm(); }} type="button"
                    data-testid="crm-preview-fu-cancel"
                    className="btn-ghost text-xs">Cancel</button>
                  <button onClick={addFollowUp} type="button" disabled={savingFu || !logForm.summary.trim()}
                    data-testid="crm-preview-fu-add"
                    className="btn-primary text-xs disabled:opacity-50">
                    {editingInteraction ? (
                      <><Pencil size={12} className="inline mr-1" /> Update Interaction</>
                    ) : (
                      <><Plus size={12} className="inline mr-1" /> Log interaction</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="mb-3 flex flex-wrap gap-2">
              <input value={historySearch} onChange={(event) => setHistorySearch(event.target.value)} placeholder="Search subject, notes, reply, platform" aria-label="Search communication history" className="gio-input min-w-[220px] flex-1" />
              {[
                ["all", "All"], ["dm", "DM"], ["email", "Email"], ["call", "Call"], ["meeting", "Meeting"], ["note", "Note"], ["other", "Other"],
                ["needs_follow_up", "Needs follow-up"], ["positive", "Positive"], ["negative", "Negative"],
              ].map(([value, label]) => <button key={value} type="button" onClick={() => setHistoryFilter(value)} aria-pressed={historyFilter === value}
                className="border px-2.5 py-1.5 text-[11px] transition"
                style={{ borderColor: historyFilter === value ? "var(--gio-primary)" : "var(--gio-border)", color: historyFilter === value ? "var(--gio-primary)" : "var(--gio-muted)", background: historyFilter === value ? "var(--gio-primary-soft)" : "transparent" }}>{label}</button>)}
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {sortedFu.length === 0 && (
                <div className="py-5 text-[13px]" style={{ color: "var(--gio-muted)" }}>No communication logged yet. Log the first interaction.</div>
              )}
              {sortedFu.length > 0 && visibleFollowUps.length === 0 && <div className="py-5 text-[13px]" style={{ color: "var(--gio-muted)" }}>No matching communications.</div>}
              {visibleFollowUps.map(f => {
                const k = FOLLOWUP_KINDS.find(x => x.v === f.kind) || FOLLOWUP_KINDS[0];
                const Icon = k.icon;
                const outcome = FU_OUTCOMES.find(o => o.v === f.outcome);
                const dir = FU_DIRECTIONS.find(d => d.v === f.direction);
                return (
                  <div key={f.id}
                    className="flex items-start gap-3 p-3 border rounded-lg"
                    data-testid={`crm-preview-fu-${f.id}`}
                    style={{
                      borderColor: f.done ? "rgba(34,197,94,0.3)" : "var(--gio-border)",
                      background: f.done ? "rgba(34,197,94,0.04)" : "var(--gio-surface)",
                    }}>
                    <button onClick={() => toggleFollowUp(f.id)} type="button"
                      data-testid={`crm-preview-fu-toggle-${f.id}`}
                      className="mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0"
                      style={{ borderColor: f.done ? "#22c55e" : "var(--gio-border)", background: f.done ? "#22c55e" : "transparent" }}>
                      {f.done && <Check size={13} style={{ color: "#fff" }} />}
                    </button>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                      <Icon size={13} />
                    </div>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        // Open interaction editor
                        const interactionData = {
                          contactId: person.id,
                          contactName: person.stage_name || person.legal_name,
                          existingInteraction: f
                        };
                        window.dispatchEvent(new CustomEvent('open-interaction-editor', { detail: interactionData }));
                      }}
                      title="Click to view/edit interaction"
                    >
                      <div className={`text-[13px] font-semibold ${f.done ? "line-through" : ""}`}
                        style={{ color: f.done ? "var(--gio-muted-3)" : "var(--gio-text)" }}>
                        {f.summary || f.note || "(no summary)"}
                      </div>
                      {f.summary && f.note && f.note !== f.summary && (
                        <div className="text-[12px] mt-0.5 whitespace-pre-wrap" style={{ color: "var(--gio-muted-2)" }}>
                          {f.note}
                        </div>
                      )}
                      {f.reply && (
                        <div className="mt-2 pl-3 border-l-2 rounded text-[12px] whitespace-pre-wrap" data-testid={`crm-preview-fu-reply-${f.id}`}
                          style={{ borderColor: "var(--gio-secondary)", background: "rgba(6,182,212,0.06)", padding: "8px 10px", color: "var(--gio-text)" }}>
                          <div className="overline mb-1" style={{ color: "var(--gio-secondary)" }}>💬 THEIR REPLY</div>
                          {f.reply}
                        </div>
                      )}
                      <div className="text-[10px] mt-1 inline-flex items-center gap-2 flex-wrap" style={{ color: "var(--gio-muted-3)" }}>
                        <span>{formatInteractionDate(f.date)}</span>
                        <span>·</span>
                        <span className="uppercase tracking-wider">{k.label}</span>
                        {f.platform && <><span>·</span><span>{f.platform}</span></>}
                        {dir && <><span>·</span><span>{dir.label.split(" ")[0]}</span></>}
                        {outcome && (
                          <>
                            <span>·</span>
                            <span style={{ color: outcome.hex }}>{outcome.label}</span>
                          </>
                        )}
                        {f.date && <><span>·</span><span>{safeRelative(f.date)}</span></>}
                        {f.follow_up_date && (
                          <span className="px-1.5 py-0.5 font-bold" style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                            Follow-up {formatInteractionDate(f.follow_up_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {f.follow_up_date && !f.done && <button onClick={() => toggleFollowUp(f.id)} type="button" className="btn-ghost text-[10px]" data-testid={`crm-preview-fu-done-${f.id}`}>Mark follow-up done</button>}
                      <button 
                        onClick={() => editInteraction(f)} 
                        type="button"
                        data-testid={`crm-preview-fu-edit-${f.id}`}
                        className="text-zinc-600 hover:text-cyan-400"
                        title="Edit interaction">
                        <Pencil size={13} />
                      </button>
                      <button 
                        onClick={() => duplicateInteraction(f)} 
                        type="button"
                        data-testid={`crm-preview-fu-duplicate-${f.id}`}
                        className="text-zinc-600 hover:text-blue-400"
                        title="Duplicate interaction">
                        <Plus size={13} />
                      </button>
                      <button onClick={() => deleteFollowUp(f.id)} type="button"
                        data-testid={`crm-preview-fu-del-${f.id}`}
                        className="text-zinc-600 hover:text-red-400">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content 360 strip — Enhanced with Catalog Integration */}
          {content360?.totals && (
            <div className="px-6 md:px-8 py-6 border-t bg-gradient-to-br from-purple-900/10 to-pink-900/10" style={{ borderColor: "rgba(168, 85, 247, 0.2)" }} data-testid="crm-content-360-strip">
              <div className="flex items-center justify-between mb-4">
                <div className="overline font-bold tracking-wider inline-flex items-center gap-2 text-purple-400">
                  <Sparkles size={16} className="text-pink-400" /> CRM 360° · CONTENT · ENGAGEMENT · REVENUE
                </div>
                <a 
                  href={`/catalog?cast=${person.stage_name || person.legal_name}`}
                  className="px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-300 text-xs font-semibold transition-all inline-flex items-center gap-2"
                >
                  📺 View in Catalog
                </a>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3" data-testid="crm-content-360-metrics">
                <Stat360Enhanced label="Videos" value={content360.totals.videos} icon="🎥" testid="c360-videos" />
                <Stat360Enhanced label="Pictures" value={content360.totals.pictures} icon="📸" testid="c360-pictures" />
                <Stat360Enhanced label="Posts" value={content360.totals.social_posts} icon="📱" testid="c360-posts" />
                <Stat360Enhanced label="Extension posts" value={content360.totals.extension_synced_posts || extensionPosts.length} icon="🧩" testid="c360-extension-posts" />
                <Stat360Enhanced label="Engagement" value={(content360.totals.engagement || 0).toLocaleString()} icon="💖" testid="c360-engagement" />
                <Stat360Enhanced label="Views" value={(content360.totals.views || 0).toLocaleString()} icon="▶" testid="c360-views" />
                <Stat360Enhanced label="Shares" value={(content360.totals.shares || 0).toLocaleString()} icon="↗" testid="c360-shares" />
                <Stat360Enhanced label="Saves" value={(content360.totals.saves || 0).toLocaleString()} icon="▣" testid="c360-saves" />
                {hasFanFinancialRelationship && <Stat360Enhanced label="Purchases" value={content360.totals.of_purchases_count || 0} icon="🛒" testid="c360-of-purchases" />}
                <Stat360Enhanced label="Revenue" value={`$${(hasFanFinancialRelationship ? (content360.totals.revenue_usd || 0) + (content360.totals.of_purchases_gross_usd || 0) : content360.totals.revenue_usd || 0).toLocaleString()}`} icon="💰" testid="c360-revenue" accent />
              </div>

              {extensionPosts.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs" data-testid="c360-extension-status"
                  style={{ borderColor: "rgba(34,197,94,0.25)", background: "rgba(34,197,94,0.06)", color: "var(--gio-muted-2)" }}>
                  <span className="font-semibold text-emerald-300">Extension synced</span>
                  <span>{extensionPosts.length} linked post{extensionPosts.length === 1 ? "" : "s"}</span>
                  {extensionLastSynced && <span>Last sync {formatSyncTime(extensionLastSynced)}</span>}
                </div>
              )}
              
              {/* Revenue Breakdown */}
              {hasFanFinancialRelationship && (content360.totals.of_purchases_gross_usd || 0) > 0 && (
                <div className="bg-black/20 border border-purple-500/20 rounded-lg p-3 mb-3">
                  <div className="text-xs font-bold text-purple-300 mb-2 uppercase tracking-wider">Revenue Breakdown</div>
                  <div className="flex flex-wrap gap-3 text-sm" data-testid="c360-of-breakdown">
                    {Object.entries(content360.totals.purchases_by_platform || {}).map(([plat, val]) => (
                      <span key={plat} className="text-zinc-300">
                        <span className="text-zinc-500">{plat}:</span> <span className="text-white font-semibold">${val}</span>
                      </span>
                    ))}
                    {(content360.totals.revenue_usd || 0) > 0 && (
                      <span className="text-zinc-300">
                        <span className="text-zinc-500">Post revenue:</span> <span className="text-pink-400 font-semibold">${content360.totals.revenue_usd}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {content360.totals.last_appearance && (
                <div className="flex items-center gap-2 text-sm px-3 py-2 bg-indigo-900/20 border border-indigo-500/20 rounded-lg" data-testid="c360-last">
                  <span className="text-indigo-400 font-semibold">Last appearance:</span>
                  <span className="text-white font-medium">{(content360.totals.last_appearance || "").slice(0, 10)}</span>
                </div>
              )}
              <p className="mt-3 text-xs text-zinc-500">Counts include content explicitly linked to this contact; unlinked platform activity is not attributed.</p>
            </div>
          )}

          {/* Linked Pictures */}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="overline mb-3 inline-flex items-center gap-2">
              🖼 LINKED PICTURES · {content360?.pictures?.length || 0}
            </div>
            {loading360 && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
            {!loading360 && (content360?.pictures || []).length === 0 && (
              <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                No pictures tagged. Open a picture in /pictures and add this contact to its crew.
              </div>
            )}
            {(content360?.pictures || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="crm-linked-pictures">
                {content360.pictures.slice(0, 18).map(p => (
                  <a key={p.id} href={`/pictures?focus=${p.id}`} target="_blank" rel="noreferrer"
                    data-testid={`crm-pic-${p.id}`}
                    className="relative aspect-square rounded-lg overflow-hidden border-2 transition hover:scale-105"
                    style={{ borderColor: "var(--gio-border)" }} title={p.title || "Untitled"}>
                    {p.image_url
                      ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      : <div className="w-full h-full bg-zinc-900 flex items-center justify-center text-zinc-600 text-xs">no img</div>
                    }
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-1.5">
                      <div className="text-[10px] text-white truncate">{p.title || "Untitled"}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Multi-Tracker rollup + expandable posts feed */}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <MultiTrackerSection content360={content360} loading={loading360} />
          </div>

          {/* Fan Purchases — cross-platform (OF PPV, JFF store, RFC per-video, DM PPV) */}
          {hasFanFinancialRelationship && <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="overline mb-3 inline-flex items-center gap-2 flex-wrap">
              💰 FAN PURCHASES · {content360?.totals?.of_purchases_count || 0}
              {(content360?.totals?.of_purchases_gross_usd || 0) > 0 && (
                <span className="text-[11px] normal-case tracking-normal ml-2" style={{ color: "var(--gio-secondary)" }}>
                  ${content360.totals.of_purchases_gross_usd} gross
                </span>
              )}
            </div>
            {!loading360 && (content360?.of_sales || []).length === 0 && (content360?.rfc_video_revenue || []).length === 0 && (
              <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                No purchases matched. Ensure the contact has <code className="font-mono-ui">onlyfans</code> / <code className="font-mono-ui">justforfans</code> handles set,
                run <b>Auto-import</b> in Finance, and confirm any RFC per-video links.
              </div>
            )}
            {((content360?.of_sales || []).length > 0 || (content360?.rfc_video_revenue || []).length > 0) && (
              <div className="space-y-1.5" data-testid="crm-fan-purchases">
                {(content360?.of_sales || []).slice(0, 20).map(s => {
                  const plat = (s.platform || "").toLowerCase();
                  const platLabel = plat === "onlyfans" ? "OF" : plat === "justforfans" ? "JFF" : (plat || "?").slice(0, 6).toUpperCase();
                  const platColor = plat === "onlyfans" ? "#3b82f6" : plat === "justforfans" ? "#eab308" : "var(--gio-muted-2)";
                  return (
                    <div key={s.id} className="border rounded p-2 flex items-center gap-3 text-[11px]"
                      data-testid={`crm-fan-purchase-${s.id}`}
                      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                      <span className="font-mono-ui shrink-0" style={{ color: "var(--gio-muted-2)" }}>
                        {(s.date || "").slice(0, 10)}
                      </span>
                      <span className="text-[10px] font-mono-ui uppercase font-bold shrink-0 px-1.5 py-0.5 rounded border"
                        style={{ borderColor: platColor, color: platColor }}>
                        {platLabel}
                      </span>
                      <span className="text-[10px] font-mono-ui uppercase font-bold shrink-0 px-1.5 py-0.5 rounded"
                        style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                        {s.type || "purchase"}
                      </span>
                      <span className="flex-1 min-w-0 truncate" style={{ color: "var(--gio-muted-2)" }}>{s.username || s.item || ""}</span>
                      <span className="shrink-0 font-mono-ui font-bold" style={{ color: "var(--gio-secondary)" }}>${s.gross_usd}</span>
                      <span className="shrink-0 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>net ${s.net_usd}</span>
                    </div>
                  );
                })}
                {(content360?.rfc_video_revenue || []).slice(0, 20).map(r => {
                  const subtype = (r.platform || "").replace("rawfuckclub_", "").toUpperCase() || "RFC";
                  return (
                    <div key={r.id} className="border rounded p-2 flex items-center gap-3 text-[11px]"
                      data-testid={`crm-rfc-video-${r.id}`}
                      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                      <span className="font-mono-ui shrink-0" style={{ color: "var(--gio-muted-2)" }}>
                        RFC
                      </span>
                      <span className="text-[10px] font-mono-ui uppercase font-bold shrink-0 px-1.5 py-0.5 rounded border"
                        style={{ borderColor: "#a855f7", color: "#a855f7" }}>
                        {subtype}
                      </span>
                      <span className="flex-1 min-w-0 truncate" style={{ color: "var(--gio-muted-2)" }} title={r.item}>{r.item || ""}</span>
                      {r.qty_sold > 0 && (
                        <span className="shrink-0 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{r.qty_sold} qty</span>
                      )}
                      {r.views > 0 && (
                        <span className="shrink-0 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{r.views.toLocaleString()} views</span>
                      )}
                      <span className="shrink-0 font-mono-ui font-bold" style={{ color: "#22c55e" }}>${(r.revenue_usd || 0).toFixed(2)}</span>
                    </div>
                  );
                })}
                {(content360?.of_sales?.length || 0) + (content360?.rfc_video_revenue?.length || 0) > 40 && (
                  <div className="text-[10px] italic text-center pt-1" style={{ color: "var(--gio-muted-3)" }}>
                    Showing 40 of {(content360.of_sales?.length || 0) + (content360.rfc_video_revenue?.length || 0)} purchases.
                  </div>
                )}
              </div>
            )}
          </div>}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <Film size={12} /> LINKED CATALOG VIDEOS · {videos.filter(v => !v.is_studio).length}
              {videos.some(v => v.is_studio) && (
                <span className="text-[10px] normal-case tracking-normal ml-2" style={{ color: "var(--gio-muted-3)" }}>
                  ({videos.filter(v => v.is_studio).length} studio video{videos.filter(v => v.is_studio).length === 1 ? "" : "s"} moved to Studio Collabs ↓)
                </span>
              )}
            </div>
            {loadingV && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
            {!loadingV && videos.filter(v => !v.is_studio).length === 0 && (
              <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                No non-studio videos linked. Open a video in the catalog and add this contact to its cast.
              </div>
            )}
            {videos.filter(v => !v.is_studio).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {videos.filter(v => !v.is_studio).map(v => (
                  <LinkedVideoCard key={v.id} video={v} />
                ))}
              </div>
            )}
          </div>

          {/* Linked Studios — union of (a) studios where this contact is linked_crm_id
              AND (b) studios discovered via video.studio_crm_id where cast_crm_ids
              contains this contact (from content-360). */}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <Building2 size={12} /> LINKED STUDIO COLLABS · {studios.length + (content360?.studio_collabs?.length || 0)}
              {(totalStudioRev > 0 || (content360?.studio_collabs || []).some(s => s.total_fee_usd > 0)) && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--gio-secondary)" }}>
                  <DollarSign size={11} /> {(totalStudioRev + (content360?.studio_collabs || []).reduce((a, s) => a + (s.total_fee_usd || 0), 0)).toLocaleString()}
                </span>
              )}
            </div>
            {loadingS && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
            {!loadingS && studios.length === 0 && (content360?.studio_collabs || []).length === 0 && (
              <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                No studios linked. Mark videos as Studio + set <code className="font-mono-ui">studio_crm_id</code>, or open a Studio project and add this actor.
              </div>
            )}
            {/* Studio collabs discovered via videos.cast_crm_ids */}
            {(content360?.studio_collabs || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="crm-studio-collabs-via-videos">
                {content360.studio_collabs.map(sc => (
                  <a key={`sc-${sc.studio_crm_id}`}
                    href={`/crm?focus=${sc.studio_crm_id}`}
                    data-testid={`crm-preview-scv-${sc.studio_crm_id}`}
                    className="group block border rounded-lg overflow-hidden transition hover:-translate-y-0.5"
                    style={{ borderColor: "#a855f7", background: "var(--gio-surface)" }}>
                    {sc.studio_photo ? (
                      <img src={sc.studio_photo} alt="" className="w-full aspect-video object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center" style={{ background: "var(--gio-surface-hi)" }}>
                        <Building2 size={26} style={{ color: "#a855f7" }} />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="text-[13px] font-bold truncate" style={{ color: "var(--gio-text)" }}>{sc.studio_name}</div>
                      <div className="flex items-center gap-2 text-[10px] mt-1 flex-wrap" style={{ color: "var(--gio-muted-2)" }}>
                        <span className="uppercase tracking-wider" style={{ color: "#a855f7" }}>STUDIO</span>
                        <span>·</span>
                        <span>{sc.video_count} video{sc.video_count !== 1 ? "s" : ""}</span>
                        {sc.total_fee_usd > 0 && (
                          <>
                            <span>·</span>
                            <span style={{ color: "var(--gio-secondary)" }}>${sc.total_fee_usd.toLocaleString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
            {studios.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {studios.map(s => {
                  const sceneCount = (s.scenes || []).length;
                  const studioRev = (s.scenes || []).reduce((a, sc) => a + (Number(sc.revenue) || 0), 0);
                  const cover = s.cover_image
                    || (s.scenes?.[0]?.mux_playback_id ? `https://image.mux.com/${s.scenes[0].mux_playback_id}/thumbnail.jpg?width=320&height=180&fit_mode=smartcrop` : null)
                    || s.photos?.[0];
                  return (
                    <a key={s.id} href={`/studios?focus=${s.id}`} target="_blank" rel="noreferrer"
                      data-testid={`crm-preview-ls-${s.id}`}
                      className="group block border rounded-lg overflow-hidden transition hover:-translate-y-0.5"
                      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                      {cover ? (
                        <img src={cover} alt="" className="w-full aspect-video object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full aspect-video flex items-center justify-center" style={{ background: "var(--gio-surface-hi)" }}>
                          <Building2 size={26} style={{ color: "var(--gio-muted-3)" }} />
                        </div>
                      )}
                      <div className="p-3">
                        <div className="text-[13px] font-bold truncate" style={{ color: "var(--gio-text)" }}>{s.studio_name}</div>
                        <div className="flex items-center gap-2 text-[10px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                          <span className="uppercase tracking-wider">{s.status}</span>
                          <span>·</span>
                          <span>{sceneCount} scene{sceneCount !== 1 ? "s" : ""}</span>
                          {studioRev > 0 && (
                            <>
                              <span>·</span>
                              <span style={{ color: "var(--gio-secondary)" }}>{studioRev.toLocaleString()} {s.fee_currency}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Collaborators — other porn actors featured in same videos */}
          <div className="px-6 md:px-8 py-6 border-t" style={{ borderColor: "var(--gio-border)" }}>
            <div className="overline mb-3 inline-flex items-center gap-2">
              🎬 COLLABORATED WITH · {content360?.collaborators?.length || 0}
            </div>
            {loading360 && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
            {!loading360 && (content360?.collaborators || []).length === 0 && (
              <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                No collaborators yet. Once this contact is added to a video&apos;s cast alongside other Models, they&apos;ll appear here.
              </div>
            )}
            {(content360?.collaborators || []).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="crm-collaborators">
                {content360.collaborators.map(c => (
                  <a key={c.crm_id}
                    href={`/crm?focus=${c.crm_id}`}
                    data-testid={`crm-collab-${c.crm_id}`}
                    className="group border rounded-lg overflow-hidden flex flex-col transition hover:-translate-y-0.5"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                    {c.photo ? (
                      <img src={c.photo} alt={c.stage_name} className="w-full aspect-square object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center text-[20px] font-bold"
                        style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>
                        {(c.stage_name || "?").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="p-2.5 flex-1">
                      <div className="text-[12px] font-bold truncate" style={{ color: "var(--gio-text)" }}>{c.stage_name}</div>
                      <div className="flex items-center gap-2 text-[10px] mt-1 flex-wrap" style={{ color: "var(--gio-muted-2)" }}>
                        {c.type && <span className="uppercase tracking-wider" style={{ color: "var(--gio-primary)" }}>{c.type.split(" ")[0]}</span>}
                        <span>·</span>
                        <span>{c.shared_videos} shared</span>
                      </div>
                      {c.last_shoot_date && (
                        <div className="text-[10px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
                          Last: {c.last_shoot_date}
                        </div>
                      )}
                      {c.sample_titles && c.sample_titles.length > 0 && (
                        <div className="text-[9px] mt-1 truncate italic" style={{ color: "var(--gio-muted-3)" }} title={c.sample_titles.join(" · ")}>
                          {c.sample_titles[0]}
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="px-6 md:px-8 py-4 border-t flex flex-wrap gap-4 text-[11px]"
            style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}>
            {person.shoot_date && <span>Shoot date: <span style={{ color: "var(--gio-muted)" }}>{person.shoot_date}</span></span>}
            {person.last_contacted_at && <span>Last contacted: <span style={{ color: "var(--gio-muted)" }}>{formatRelative(person.last_contacted_at)}</span></span>}
            {person.created_at && <span>Created: <span style={{ color: "var(--gio-muted)" }}>{(person.created_at || "").slice(0, 10)}</span></span>}
            {person.updated_at && <span>Updated: <span style={{ color: "var(--gio-muted)" }}>{formatRelative(person.updated_at)}</span></span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PStat({ label, value, icon: Icon, highlight }) {
  if (!value) return null;
  return (
    <div className="border rounded-lg px-3 py-2"
      style={{
        borderColor: highlight ? "var(--gio-primary)" : "var(--gio-border)",
        background: highlight ? "var(--gio-primary-soft)" : "var(--gio-surface)",
      }}>
      <div className="overline inline-flex items-center gap-1" style={{ color: highlight ? "var(--gio-primary)" : undefined }}>
        {Icon && <Icon size={9} />} {label}
      </div>
      <div className="text-[14px] font-bold mt-0.5 truncate" style={{ color: highlight ? "var(--gio-primary)" : "var(--gio-text)" }}>
        {value}
      </div>
    </div>
  );
}

function PBlock({ label, value, accent }) {
  if (!value) return null;
  return (
    <div>
      <div className="overline mb-1.5" style={{ color: accent }}>{label}</div>
      <div className="text-[13px] whitespace-pre-wrap" style={{ color: "var(--gio-text)" }}>{value}</div>
    </div>
  );
}

function SocialChip({ href, label, color, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-full border transition hover:-translate-y-0.5"
      style={{
        borderColor: color || "var(--gio-border)",
        color: color || "var(--gio-text)",
        background: "var(--gio-surface)",
      }}>
      {label} <ExternalLink size={10} />
    </a>
  );
}


function Stat360({ label, value, testid, accent }) {
  return (
    <div data-testid={testid}
      className="rounded-lg border p-3 flex flex-col gap-1"
      style={{
        borderColor: accent ? "var(--gio-secondary)" : "var(--gio-border)",
        background: "var(--gio-surface)",
      }}>
      <div className="text-[10px] font-mono-ui uppercase tracking-wider"
        style={{ color: "var(--gio-muted-2)" }}>{label}</div>
      <div className="text-[18px] font-bold leading-none"
        style={{ color: accent ? "var(--gio-secondary)" : "var(--gio-text)" }}>
        {value}
      </div>
    </div>
  );
}


/**
 * MultiTrackerSection — replaces the old "Social Posts Tagged" list.
 * Shows a per-platform rollup (posts count / engagement / revenue) as pill chips,
 * plus an expandable full list below.
 */
function LinkedVideoCard({ video }) {
  const platformStatuses = [
    ["OF VIP", video.of_vip_status],
    ["OF Free", video.of_free_status],
    ["JFF", video.jff_status],
    ["RFC", video.rfc_status],
    ["Faphouse", video.faphouse_status],
    ["Top4Fans", video.top4fans_status],
  ].filter(([, status]) => status && status !== "none");
  const tags = Array.isArray(video.tags) ? video.tags.filter(Boolean) : [];
  const scenePhoto = video.scene_photos?.[0];
  const facts = [
    video.shoot_date && formatDateOnly(video.shoot_date),
    [video.city, video.location].filter(Boolean).join(" · "),
    video.runtime_minutes && `${video.runtime_minutes} min`,
  ].filter(Boolean);

  return (
    <div data-testid={`crm-preview-lv-${video.id}`} className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
      {video.mux_playback_id ? (
        <div className="bg-black" data-testid={`crm-preview-lv-player-${video.id}`}>
          <MuxPlayer
            playbackId={video.mux_playback_id}
            metadata={{ video_id: video.id, video_title: video.title, viewer_user_id: "crm_preview" }}
            accentColor="#14B8A6"
            style={{ width: "100%", aspectRatio: "16/9" }}
            streamType="on-demand"
          />
        </div>
      ) : (
        <div className="relative flex aspect-video items-center justify-center bg-black">
          {scenePhoto ? <img src={scenePhoto} alt={`${video.title || "Video"} still`} className="h-full w-full object-cover" loading="lazy" /> : <Film size={26} style={{ color: "var(--gio-muted-3)" }} />}
          <span className="absolute bottom-2 left-2 rounded bg-black/75 px-2 py-1 text-[10px] text-zinc-300">No preview</span>
        </div>
      )}
      <div className="space-y-2.5 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-bold" style={{ color: "var(--gio-text)" }} title={video.title}>{video.title || "Untitled video"}</div>
            <div className="mt-0.5 truncate text-xs" style={{ color: "var(--gio-muted-2)" }}>{video.cast || "Cast not listed"}</div>
          </div>
          <a href={`/catalog?focus=${video.id}`} target="_blank" rel="noreferrer"
            aria-label={`Open ${video.title || "video"} in catalog`}
            data-testid={`crm-preview-lv-open-${video.id}`}
            className="shrink-0 rounded border px-2 py-1 text-[10px] font-semibold uppercase transition hover:-translate-y-0.5"
            style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }}>
            Open ↗
          </a>
        </div>
        {facts.length > 0 && <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>
          {facts.map((fact, index) => <span key={`${fact}-${index}`}>{fact}</span>)}
        </div>}
        <div className="flex flex-wrap gap-1.5">
          {video.edit_status && <span className="rounded border px-2 py-0.5 text-[10px] capitalize" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>{video.edit_status.replace(/_/g, " ")}</span>}
          {platformStatuses.map(([platform, status]) => <span key={platform} className="rounded border px-2 py-0.5 text-[10px]" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>{platform}: {String(status).replace(/_/g, " ")}</span>)}
        </div>
        {tags.length > 0 && <div className="flex flex-wrap gap-1" data-testid={`crm-preview-lv-tags-${video.id}`}>
          {tags.slice(0, 5).map((tag, index) => <span key={`${tag}-${index}`} className="rounded bg-black/25 px-1.5 py-0.5 text-[10px]" style={{ color: "var(--gio-muted-2)" }}>{tag}</span>)}
          {tags.length > 5 && <span className="px-1.5 py-0.5 text-[10px] text-zinc-500">+{tags.length - 5}</span>}
        </div>}
      </div>
    </div>
  );
}

function MultiTrackerSection({ content360, loading }) {
  const [expanded, setExpanded] = useState(false);
  const posts = content360?.social_posts || [];

  // Aggregate per platform
  const platMeta = {
    twitter: { c: "#1DA1F2", l: "Twitter" },
    instagram: { c: "#E1306C", l: "Instagram" },
    tiktok: { c: "#000", l: "TikTok" },
    onlyfans: { c: "#00AFF0", l: "OnlyFans" },
    onlyfans_vip: { c: "#00AFF0", l: "OF · VIP" },
    onlyfans_free: { c: "#22c55e", l: "OF · Free" },
    justforfans: { c: "#e11d48", l: "JustForFans" },
    rawfuckclub: { c: "#f59e0b", l: "Raw Fuck Club" },
    faphouse: { c: "#8b5cf6", l: "Faphouse" },
  };
  const meta = (p) => platMeta[p] || { c: "#94a3b8", l: (p || "other").replace(/_/g, " ") };

  const rollup = {};
  for (const p of posts) {
    const plat = p.platform || "other";
    const b = rollup[plat] || { posts: 0, likes: 0, comments: 0, shares: 0, saves: 0, views: 0, revenue: 0, extensionPosts: 0 };
    b.posts += 1;
    b.likes += Number(p.likes || 0);
    b.comments += Number(p.comments || 0);
    b.shares += Number(p.shares || 0);
    b.saves += Number(p.saves || 0);
    b.views += Number(p.views || 0);
    b.revenue += Number(p.ppv_revenue || 0) + Number(p.tips_received || 0);
    if (p.source === "extension_synced") b.extensionPosts += 1;
    rollup[plat] = b;
  }
  const rollupEntries = Object.entries(rollup).sort(([, a], [, b]) => (b.revenue + b.likes) - (a.revenue + a.likes));
  const listToShow = expanded ? posts : posts.slice(0, 5);

  return (
    <div data-testid="crm-multitracker">
      <div className="overline mb-3 inline-flex items-center gap-2">
        📊 MULTI-TRACKER · {posts.length}
      </div>

      {!loading && posts.length === 0 && (
        <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
          Nothing tracked yet for this contact. Tag them in a post or link an OF Post Report to see rollups here.
        </div>
      )}

      {rollupEntries.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="crm-multitracker-rollup">
          {rollupEntries.map(([plat, r]) => {
            const m = meta(plat);
            return (
              <div key={plat}
                data-testid={`crm-mt-rollup-${plat}`}
                className="inline-flex flex-col gap-0.5 px-3 py-1.5 rounded-lg border"
                style={{ borderColor: m.c, background: `${m.c}0f` }}>
                <div className="text-[10px] font-mono-ui uppercase font-bold" style={{ color: m.c }}>
                  {m.l}
                </div>
                <div className="text-[11px] flex flex-wrap gap-2" style={{ color: "var(--gio-text)" }}>
                  <span>{r.posts} posts</span>
                  {r.extensionPosts > 0 && <span className="text-emerald-300">{r.extensionPosts} synced</span>}
                  {r.likes > 0 && <span>Likes {r.likes.toLocaleString()}</span>}
                  {r.comments > 0 && <span>Comments {r.comments.toLocaleString()}</span>}
                  {r.shares > 0 && <span>Shares {r.shares.toLocaleString()}</span>}
                  {r.saves > 0 && <span>Saves {r.saves.toLocaleString()}</span>}
                  {r.views > 0 && <span>Views {r.views.toLocaleString()}</span>}
                  {r.revenue > 0 && <span style={{ color: "var(--gio-secondary)", fontWeight: 600 }}>${r.revenue.toLocaleString()}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {posts.length > 0 && (
        <>
          <div className="space-y-2" data-testid="crm-multitracker-list">
            {listToShow.map(p => {
              const m = meta(p.platform);
              const extensionSynced = p.source === "extension_synced";
              const extensionTags = Array.isArray(p.extension_tags) ? p.extension_tags : [];
              return (
                <div key={p.id} className="border rounded-lg p-3 flex items-start gap-3"
                  data-testid={`crm-post-${p.id}`}
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                  <span className="text-[10px] font-mono-ui uppercase font-bold shrink-0 px-2 py-0.5 rounded"
                    style={{ background: `${m.c}22`, color: m.c }}>
                    {m.l}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                      {extensionSynced && <span className="text-[9px] uppercase font-bold rounded px-1.5 py-0.5 text-emerald-300 bg-emerald-500/10">Extension synced</span>}
                      {p.source_status && <span className="text-[9px] rounded px-1.5 py-0.5" style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>{p.source_missing ? "Missing at source" : p.source_status.replace(/_/g, " ")}</span>}
                      {p.platform_access && <span className="text-[9px] rounded px-1.5 py-0.5" style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>Access: {p.platform_access}</span>}
                    </div>
                    <div className="text-[12px] line-clamp-2" style={{ color: "var(--gio-text)" }}>
                      {p.caption || "(no caption)"}
                    </div>
                    <div className="text-[10px] mt-1 flex flex-wrap gap-3" style={{ color: "var(--gio-muted-3)" }}>
                      {p.posted_at && <span>{formatDateOnly(p.posted_at)}</span>}
                      {p.likes > 0 && <span>Likes {Number(p.likes).toLocaleString()}</span>}
                      {p.comments > 0 && <span>Comments {Number(p.comments).toLocaleString()}</span>}
                      {p.shares > 0 && <span>Shares {Number(p.shares).toLocaleString()}</span>}
                      {p.saves > 0 && <span>Saves {Number(p.saves).toLocaleString()}</span>}
                      {p.views > 0 && <span>Views {Number(p.views).toLocaleString()}</span>}
                      {p.ppv_revenue > 0 && <span style={{ color: "var(--gio-secondary)" }}>${p.ppv_revenue}</span>}
                      {p.tips_received > 0 && <span style={{ color: "var(--gio-secondary)" }}>tips ${p.tips_received}</span>}
                    </div>
                    {extensionSynced && (p.last_synced_at || p.source_captured_at) && (
                      <div className="mt-1 text-[10px] text-zinc-500">{p.last_synced_at ? `Synced ${formatSyncTime(p.last_synced_at)}` : `Captured ${formatSyncTime(p.source_captured_at)}`}</div>
                    )}
                    {extensionTags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid={`crm-post-tags-${p.id}`}>
                        {extensionTags.map((tag, index) => <span key={`${tag}-${index}`} className="rounded border px-1.5 py-0.5 text-[9px]" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>{tag}</span>)}
                      </div>
                    )}
                  </div>
                  {p.post_url && (
                    <a href={p.post_url} target="_blank" rel="noreferrer"
                      className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded border"
                      style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }}>
                      Open ↗
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          {posts.length > 5 && (
            <button onClick={() => setExpanded(v => !v)}
              data-testid="crm-multitracker-toggle"
              className="mt-3 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded border hover:-translate-y-0.5 transition"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
              {expanded ? "Show less" : `Show all ${posts.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}


// Enhanced Stat Component for Content 360
function TalentSignal({ label, value }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--gio-muted-3)" }}>{label}</div>
      <div className="text-sm font-bold mt-1 truncate" style={{ color: "var(--gio-text)" }}>{value}</div>
    </div>
  );
}

function Stat360Enhanced({ label, value, icon, testid, accent }) {
  return (
    <div 
      data-testid={testid}
      className={`rounded-lg border p-3 transition-all hover:scale-105 ${
        accent 
          ? "border-pink-500/40 bg-gradient-to-br from-pink-900/20 to-purple-900/20 shadow-lg shadow-pink-500/20" 
          : "border-zinc-800 bg-black/40 backdrop-blur-sm hover:border-purple-500/30"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className="text-base">{icon}</span>}
        <div className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">
          {label}
        </div>
      </div>
      <div className={`text-xl font-bold leading-none ${
        accent ? "bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent" : "text-white"
      }`}>
        {value}
      </div>
    </div>
  );
}
